import { ReviewCommentRecord } from '@mesa/core/review';
import React, { useState, useRef } from 'react';

import { MesaDiff } from './diff';
import { FileHeader } from './file-header';

import type { DiffHunk } from '@/lib/diff-parser';
import { cn } from '@/lib/utils';

export interface MesaCodeReviewBoxProps {
  filePath: string;
  hunks: DiffHunk[];
  viewed?: boolean;
  className?: string;
  viewMode?: 'split' | 'unified';
  /**
   * Whether the file is expanded
   * @default true
   */
  open?: boolean;
  /**
   * Callback when the expand state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Callback when the viewed state changes
   */
  onViewedChange?: (viewed: boolean) => void;
  /**
   * Array of review comments for this file
   */
  reviewComments?: ReviewCommentRecord[];
  /**
   * The owner of the repository
   */
  owner?: string;
  /**
   * The repository name
   */
  repo?: string;
  /**
   * The commit SHA
   */
  sha?: string;
  /**
   * Offset from the top for sticky header positioning
   * @default 0
   */
  stickyOffset?: number;
}

export function MesaCodeReviewBox({
  filePath,
  hunks,
  viewed = false,
  className,
  viewMode,
  open,
  onOpenChange,
  onViewedChange,
  owner,
  repo,
  sha,
  stickyOffset = 0,
}: MesaCodeReviewBoxProps) {
  // Use internal state if open/onOpenChange props are not provided
  const [internalExpanded, setInternalExpanded] = useState(true);
  const diffRef = useRef<HTMLDivElement>(null);

  // Use props if provided, otherwise use internal state
  const expanded = open !== undefined ? open : internalExpanded;
  const handleExpandToggle = () => {
    if (onOpenChange) {
      onOpenChange(!expanded);
    } else {
      setInternalExpanded(!expanded);
    }
  };

  const handleViewedToggle = () => {
    if (onViewedChange) {
      onViewedChange(!viewed);
    }
  };

  return (
    <div
      className={cn('w-full border border-border rounded-sm flex flex-col contain-paint', className)}
      data-file-path={filePath}
    >
      <div
        className={cn('w-full sticky bg-muted border-border contain-paint z-20', expanded && 'border-b')}
        style={{ top: `${stickyOffset}px` }}
      >
        <FileHeader
          filePath={filePath}
          expanded={expanded}
          onToggleExpand={handleExpandToggle}
          viewed={viewed}
          onToggleViewed={handleViewedToggle}
          owner={owner}
          repo={repo}
          sha={sha}
        />
      </div>
      <div
        ref={diffRef}
        className="overflow-hidden"
        style={{ visibility: expanded ? 'visible' : 'hidden', height: expanded ? 'auto' : 0 }}
      >
        <MesaDiff hunks={hunks} viewMode={viewMode} filename={filePath} owner={owner} repo={repo} sha={sha} />
      </div>
    </div>
  );
}
